import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { toast } from "react-toastify";
import { DeleteIcon, LeftArrow } from "../../assets/icons/icons";
import MediaListNav from "../Navbar/MediaListNav";
import Pagination from "../pagination/Pagination";
import { PaginationSkeleton } from "../pagination/PaginationSkeleton";

import { deleteCategory, getCategories } from "../../api/faceswap/category";
import {
  updateMultipleTemplates,
  updatePremiumTemplates,
} from "../../api/faceswap/categoryTemplate";
import {
  addCategoryWithTemplates,
  deleteTemplate,
} from "../../api/faceswap/template";
import { useSelectedOption } from "../../context/media-context/SelectedOptionContext";
import MediaLayout from "../../layout/MediaLayout";
import Button from "../common/button";
import Dropdown from "../common/dropdown";
import AddNewCategory from "../faceswap/category/dialogbox/AddNewCategory";
import MessageDisplay from "../common/ErrorDisplay";
import DeleteSpecificMedia from "../common/DeleteSpecificMedia";
import WarningModal from "../common/warning-modal/WarningModal";

export default function MediaList({ mediaType, media, isLoading }) {
  const location = useLocation();
  const navigate = useNavigate();

  // Constants derived from props and location

  // State variables
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryOrder = searchParams.get("category_order");
  const categoryName = searchParams.get("category_name");
  const [pagination, setItemsPerPage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingState, setLoadingState] = useState({}); // Track loading state for each image
  const [allValues, setAllValues] = useState([]);
  const [resetState, setResetState] = useState(false);
  const { mediaId } = useParams();
  // console.log("media id-->", typeof mediaId, mediaId);
  // Refs
  const scrollRef = useRef(null);

  // Variables derived from props and state
  const templates = categoryOrder ? media?.templates : media?.templates;
  const paginationInfo = media?.pagination_info;
  const totalPages = paginationInfo?.total_pages || 1;
  const page = searchParams.get("page") || "";
  const queryClient = useQueryClient();

  useEffect(() => {
    const pageParam = searchParams.get("page") || 1;
    setCurrentPage(Number(pageParam));
  }, [searchParams]);

  const routeToGo = location.pathname.includes("photos") ? "photos" : "videos";
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Ensure fallback values in case variables are undefined or null
    const categoryOrderValue = categoryOrder || ""; // Default to empty if undefined
    const categoryNameValue = categoryName || ""; // Default to empty if undefined
    const paginationValue = pagination || 10; // Default to empty if undefined

    // Check if the current route is a nested route
    const isNestedRoute = location.pathname.includes(`faceswap/${routeToGo}/`);
    const newPath = `/faceswap/${routeToGo}?category_order=${categoryOrderValue}&category_name=${categoryNameValue}&page=${page}&pagination=${paginationValue}`;

    if (isNestedRoute) {
      navigate(newPath); // Navigate to the parent route without the id
    } else {
      navigate(newPath); // Navigate to the updated page with query params
    }
  };

  const handleImageLoad = (id) => {
    setLoadingState((prev) => ({ ...prev, [id]: false })); // Mark the specific image as loaded
  };

  const handleImageClick = (id) => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const isVisitedAndEdit = (id) => {
    return allValues.some((item) => item.id === id);
  };

  const isAllValueEmpty = () => {
    return allValues.length === 0;
  };

  const handleReset = () => {
    setAllValues([]);
  };

  useEffect(() => {
    if (resetState) {
      setAllValues([]);
      setResetState(false);
    }
  }, [resetState]);

  const updateTemplates = useMutation({
    mutationFn: (payload) => updateMultipleTemplates(payload),
    onSuccess: () => {
      queryClient.invalidateQueries([
        "getSpecificCategoryTemplate",
        categoryOrder,
      ]);
      toast.success("Template updated successfully!");
      // setResetState(true); // Trigger the state reset after success
    },
    onError: (error) => {
      toast.error(`Error updating template: ${error.message}`);
    },
  });

  const handleSave = () => {
    const payload = allValues;
    updateTemplates.mutate(payload);
  };

  // Media List Nav 01
  const { selectedOption, setSelectedOption } = useSelectedOption();
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [isSavingData, setIsSavingData] = useState(false);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ["getCategories"],
    queryFn: getCategories,
  });
  data?.sort((a, b) => {
    if (a.category_name < b.category_name) return -1;
    if (a.category_name > b.category_name) return 1;
    return 0;
  });

  const handleOpen = () => {
    setIsOpenDialog(true); // Open the AddNewCategory dialog
  };

  const handleCloseDialog = () => {
    setIsOpenDialog(false); // Close the dialog
  };

  const handleToggleDropdown = () => {
    setOpenDropdownIndex(!openDropdownIndex);
  };
  const actions = [
    {
      component: <Button handleOpen={handleOpen}>Create New</Button>,
    },
    {
      component: (
        <Dropdown
          buttonText="All"
          items={data}
          className="w-[130px] bg-[#0075FF1A] text-[#000000]"
          isOpen={openDropdownIndex}
          toggleDropdown={() => handleToggleDropdown()}
          shouldNavigate={true}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          routeToGo={routeToGo}
        />
      ),
    },
  ];
  const mutation = useMutation({
    mutationFn: (payload) => addCategoryWithTemplates(payload),
    onSuccess: (data, restParams) => {
      setIsSavingData(false);
      toast.success("Data submitted");
      restParams.closeDialog();
      // Refetch the relevant query (e.g., 'categories')
      queryClient.invalidateQueries(["getCategories"]);
    },
    onError: (error) => {
      setIsSavingData(false);
      toast.error("Failed to submit data");
      console.error(error);
    },
  });
  const handleAddNewCategory = (data, closeDialog) => {
    try {
      mutation.mutate({ ...data, closeDialog });
    } catch (error) {
      console.log("error===>", error);
    }
  };
  const [deleteImage, setDeleteImage] = useState(null);
  const handleDeleteMedia = (id) => {
    setDeleteImage(id);
    setIsWarningModalOpen(true); // Open the confirmation modal
  };
  // Constants and state
  const deleteTemplateMutation = useMutation({
    mutationFn: deleteTemplate,
    onSuccess: () => {
      if (mediaId) {
        navigate(
          `/faceswap/${routeToGo}?category_order=${categoryOrder || ""}&page=${page}&pagination=${pagination || 10}`,
        );
      }
      if (categoryOrder) {
        // Invalidate specific category template query
        queryClient.invalidateQueries([
          "getSpecificCategoryTemplate",
          categoryOrder,
          page,
          pagination,
        ]);
      } else {
        // Invalidate random templates query
        queryClient.invalidateQueries(["getRandomTemplates", page, pagination]);
      }
      toast.success("Item deleted successfully!");
    },
    onError: (error) => {
      toast.error(`Error deleting category: ${error.message}`);
    },
  });

  const handleDeleteConfirm = () => {
    let id = deleteImage;
    deleteTemplateMutation.mutate(id);

    // setIsWarningModalOpen(false); // Close the modal after deletion
  };

  const handleDeleteCancel = () => {
    setIsWarningModalOpen(false); // Close the modal without deleting
  };

  return (
    <>
      <MediaLayout
        mediaNav={
          <>
            {mediaId ? (
              <div className="flex items-center justify-between border-b border-[#0075FF1A] px-[30px] pb-[30px] pt-[30px]">
                <div onClick={() => navigate(-1)} className="cursor-pointer">
                  <LeftArrow />
                </div>
                <div className="flex flex-row gap-4">
                  <button
                    onClick={handleReset}
                    className={`rounded-[10px] px-[23px] py-[12px] text-center font-outfit text-[16px] font-medium leading-[20.16px] text-white ${
                      isAllValueEmpty()
                        ? "cursor-not-allowed bg-[#0075FF] opacity-50"
                        : "cursor-pointer bg-[#0075FF]"
                    }`}
                    disabled={isAllValueEmpty()}
                  >
                    Reset
                  </button>
                  <button
                    onClick={handleSave}
                    className={`rounded-[10px] px-[23px] py-[12px] text-center font-outfit text-[16px] font-medium leading-[20.16px] text-white ${
                      isAllValueEmpty()
                        ? "cursor-not-allowed bg-[#0075FF] opacity-50"
                        : "cursor-pointer bg-[#0075FF]"
                    }`}
                    disabled={isAllValueEmpty()}
                  >
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <MediaListNav
                title={mediaType === "photos" ? "All Photos" : "All Videos"}
                actions={actions}
              />
            )}
          </>
        }
      >
        <div
          ref={scrollRef}
          className="scrollbar-hide h-[calc(100vh-350px)] overflow-hidden overflow-y-auto"
        >
          <Outlet context={{ allValues, setAllValues, handleDeleteMedia }} />
          {templates?.length != 0 ? (
            <div className="grid grid-cols-3 gap-4 gap-x-[33px] gap-y-[30px] px-[30px] py-[30px] xl:grid-cols-4 2xl:grid-cols-5">
              {templates?.map((image, index) => {
                const isImageLoading = loadingState[image.id] !== false; // Check if specific image is still loading

                return (
                  <div key={index}>
                    {isImageLoading && (
                      <div className="h-[260px] w-[210px] animate-pulse rounded-md bg-gray-200"></div> // Skeleton loader
                    )}
                    <div className="group relative">
                      <Link
                        key={image.id}
                        to={
                          categoryOrder
                            ? //route from all photos or videos
                              `${image.id}?category_order=${categoryOrder}&page=${page || 1}`
                            : //route from all photos or videos
                              `${image.id}?pagination=${pagination}&page=${page || 1}`
                        }
                        onClick={() => handleImageClick(image.id)}
                      >
                        <img
                          src={`https://faceswap.cognise.art${image.thumbnail}`}
                          alt={`${mediaType} Thumbnail`}
                          className={`h-[260px] w-[210px] rounded-md ${
                            isVisitedAndEdit(image.id) &&
                            "border-4 border-blue-500"
                          }`}
                          onLoad={() => handleImageLoad(image.id)} // Mark image as loaded when it finishes loading
                          style={{ display: isImageLoading ? "none" : "block" }} // Hide image while loading
                        />
                      </Link>

                      {/* Media is add to not show deleteSpecificIcon in nested routes */}
                      {/* {!mediaId && ( */}
                      <DeleteSpecificMedia
                        className="left-3 top-3 z-10 rounded-md bg-red-200 p-1"
                        onClick={() => handleDeleteMedia(image.id)}
                      />
                      {/* )} */}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <MessageDisplay type="info" message="No Data Found!" />
          )}
        </div>

        <div className="absolute bottom-5 left-0 flex w-full justify-center">
          {isLoading ? (
            <PaginationSkeleton />
          ) : (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              pagination={pagination}
              setItemsPerPage={setItemsPerPage}
            />
          )}
        </div>
      </MediaLayout>

      <AddNewCategory
        isOpen={isOpenDialog}
        setIsSavingData={setIsSavingData}
        isSavingData={isSavingData}
        onClose={handleCloseDialog}
        data={data}
        handleAddNewCategory={handleAddNewCategory}
      />

      <WarningModal
        isOpen={isWarningModalOpen}
        setIsOpen={setIsWarningModalOpen}
        onConfirm={() => handleDeleteConfirm()} // Call delete function on confirmation
        onCancel={handleDeleteCancel} // Close modal without deleting on cancel
      />
    </>
  );
}
